import React, {useEffect, useState} from 'react'
import UiInputText from "../components/Form/UiInputText";
import Form from "../components/Form/Form";
import {useForm, FormProvider} from "react-hook-form";
import WindowBody from '../components/Window/WindowBody';
import WindowFooter from '../components/Window/WindowFooter';
import { UiButton } from '../components/UiButton';
import UiInputList from "../components/Form/UiInputList";
import UiInputSuggest from "../components/Form/UiInputSuggest";
import UiInputCheckbox from "../components/Form/UiInputCheckbox";
import UiInputTextarea from "../components/Form/UiInputTextarea";
import UiGridFormTable from "../components/Form/UiGridFormTable";
import UIFotoUploadV2 from "../components/Form/UIFotoUploadV2";
import Overlay from "../components/Overlay";
import {get} from "../services/ApiService";
import "../components/Form/UIUploadShared.css";

const tipoList = [
    { id: 'produto', title: 'Produto' },
    { id: 'kit', title: 'Kit' },
];

interface CaracteristicaDef {
    id: number;
    nome: string;
    slug: string;
    tipo: string;
    unidade: string | null;
    opcoes: string[] | null;
    obrigatorio: boolean;
    ordem: number;
}

const toDefaults = (data: any) => {
    if (!data) {
        return {
            tipo: 'produto',
            ativo: true,
            caracteristicaValores: {},
            midiaFotos: [],
            kitItemList: [],
        };
    }
    return {
        caracteristicaValores: {},
        ...data,
    };
};

function CaracteristicaCampo({ def }: { def: CaracteristicaDef }) {
    const name = `caracteristicaValores.${def.id}`;
    let label = def.unidade ? `${def.nome} (${def.unidade})` : def.nome;
    if (def.obrigatorio) label = `${label} *`;

    switch (def.tipo) {
        case 'numero':
            return <UiInputText size={4} name={name} label={label} type="number" step="any"/>;
        case 'booleano':
            return <UiInputCheckbox size={4} name={name} label={label}/>;
        case 'lista':
            return <UiInputList size={4} name={name} label={label} idField="id" titleField="title"
                                list={(def.opcoes || []).map((opcao: string) => ({ id: opcao, title: opcao }))}/>;
        default:
            return <UiInputText size={4} name={name} label={label}/>;
    }
}

export default function ProdutoForm(props: any){
    const methods = useForm({
        defaultValues: toDefaults(props.data),
        shouldUnregister: false,
    });
    const { handleSubmit, watch, setValue, reset } = methods;

    useEffect(() => {
        if (props.data) {
            reset(toDefaults(props.data));
        }
    }, [props.data, reset]);

    const allData = watch();
    const tipo = watch('tipo');
    const categoriaId = watch('categoria.id');

    const [caracteristicasPorCategoria, setCaracteristicasPorCategoria] = useState<Record<string, CaracteristicaDef[]>>({});

    useEffect(() => {
        if (!categoriaId) return;
        const key = String(categoriaId);
        if (caracteristicasPorCategoria[key]) return;
        get(`/?route=produto/caracteristicas&categoria=${categoriaId}`)
            .then((response: any) => {
                if (response.status === 200 && response.data?.success) {
                    setCaracteristicasPorCategoria((prev) => ({ ...prev, [key]: response.data.payload || [] }));
                }
            })
            .catch((error) => {
                console.error('Erro ao carregar características:', error);
            });
    }, [categoriaId, caracteristicasPorCategoria]);

    const caracteristicas = categoriaId ? caracteristicasPorCategoria[String(categoriaId)] : undefined;

    const kitItemList = watch('kitItemList');
    useEffect(() => {
        if (!Array.isArray(kitItemList)) return;
        kitItemList.forEach((item: any, index: number) => {
            if (item && (item.quantidade === undefined || item.quantidade === null || item.quantidade === '')) {
                setValue(`kitItemList.${index}.quantidade`, 1);
            }
        });
    }, [kitItemList, setValue]);

    if (props.loading) {
        return <Overlay show={true} />;
    }

    return (
        <>
            <FormProvider {...methods}>
                <Form onSubmit={handleSubmit(props.triggerSubmit)} methods={methods}>
                    <WindowBody>
                        <div className="row">
                            <div className="col-12 col-lg-6">
                                <fieldset>
                                    <legend>Dados</legend>
                                    <div className="row">
                                        <UiInputList size={4} name="tipo" list={tipoList} idField="id" titleField="title" label="Tipo" placeholder={false}/>
                                        <UiInputText size={8} name="nome" label="Nome"/>
                                        <UiInputText size={6} name="sku" label="SKU"/>
                                        <UiInputText size={6} name="referencia" label="Referência (fabricante)"/>
                                        <UiInputSuggest size={6} router="marca" idField="id" titleField="nome" name="marca" label="Marca"/>
                                        <UiInputSuggest size={6} router="categoria" idField="id" titleField="nome" name="categoria" label="Categoria"/>
                                        <UiInputCheckbox size={12} name="ativo" label="Ativo"/>
                                    </div>
                                </fieldset>

                                <fieldset>
                                    <legend>Descrição</legend>
                                    <div className="row">
                                        <UiInputTextarea size={12} name="descricao" label="Descrição" rows={3}/>
                                    </div>
                                </fieldset>
                            </div>

                            <div className="col-12 col-lg-6">
                                <fieldset style={!allData?.id ? { position: 'relative' } : undefined}>
                                    <legend>Fotos</legend>
                                    {!allData?.id && (
                                        <div className="upload-bloqueado-overlay">
                                            <div className="upload-bloqueado-message">
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                <p>Salve o produto primeiro para adicionar fotos</p>
                                            </div>
                                        </div>
                                    )}
                                    <UIFotoUploadV2
                                        name="midiaFotos"
                                        contId={allData?.id}
                                        uploadRoute="produto/form&action=fotoUpload"
                                        idParam="prd_id"
                                        showTipo={false}
                                        dropzoneLast
                                    />
                                </fieldset>
                            </div>
                        </div>

                        <fieldset>
                            <legend>Características</legend>
                            { !categoriaId &&
                                <p className="text-muted">Selecione uma categoria para carregar as características</p>
                            }
                            { categoriaId && !caracteristicas &&
                                <p className="text-muted">Carregando características...</p>
                            }
                            { categoriaId && caracteristicas && caracteristicas.length === 0 &&
                                <p className="text-muted">A categoria selecionada não possui características definidas</p>
                            }
                            { categoriaId && caracteristicas && caracteristicas.length > 0 &&
                                <div className="row">
                                    { caracteristicas.map((def) => (
                                        <CaracteristicaCampo key={def.id} def={def}/>
                                    ))}
                                </div>
                            }
                        </fieldset>

                        { tipo === 'kit' &&
                            <fieldset>
                                <legend>Composição do Kit</legend>
                                <div className="row">
                                    <UiGridFormTable name="kitItemList" label="Itens do Kit">
                                        <UiInputSuggest size={9} router="produto" idField="id" titleField="nome" name="produto" label="Produto"/>
                                        <UiInputText size={3} name="quantidade" label="Quantidade" type="number" min={0} step="any"/>
                                    </UiGridFormTable>
                                    <div className="col-lg-12">
                                        <small className="text-muted">Itens devem ser produtos simples; o estoque do kit deriva dos itens.</small>
                                    </div>
                                </div>
                            </fieldset>
                        }
                    </WindowBody>

                    <WindowFooter>
                        <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
                        <UiButton variant="primary" type="submit">Salvar</UiButton>
                        { allData?.id &&
                            <UiButton variant="primary" onClick={() => { props.triggerSubmitAsNew(allData) }}>Salvar como novo</UiButton>
                        }
                    </WindowFooter>
                </Form>
            </FormProvider>
        </>
    )
}
